import React, { useEffect, useMemo, useState } from 'react'
import classes from "./HomeScreen.module.css";
import { FaChartLine, FaBookAtlas, FaChartColumn, FaRepeat, FaBolt, FaUserCheck, FaArrowRightToBracket, FaLocationArrow, FaChevronLeft } from "react-icons/fa6";
import { TbLayoutSidebarLeftCollapseFilled } from "react-icons/tb";
import { Dropdown } from 'primereact/dropdown';
import { MultiSelect } from 'primereact/multiselect';
import { Calendar } from 'primereact/calendar';
import { FaStarOfLife, FaMinus, FaSearch } from "react-icons/fa";
import { TabPanel, TabView } from 'primereact/tabview';
import BarGraph from '../components/Graphs/BarGraph';
import LineGraph from '../components/Graphs/LineGraph';
import VerticalBarGraph from '../components/Graphs/VerticalBarGraph';
import ExportRevenueToExcel from '../components/Graphs/ExportRevenueToExcel';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { fetchAllServiceLiveRevenueApi, fetchAllServiceRevenueApi, fetchLiveDataApi, fetchRevenueApi, getClientServicesApi } from '../http/http';
import moment from "moment/moment";
import RevenueTable from '../components/Graphs/RevenueTable';
import Spinner from '../components/Spinner';
import Layout from '../components/Layout';
import Header from '../components/Header';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { removeCredentials } from '../store/slices/credentialsSlice';
import { useNavigate } from 'react-router-dom';
import ErrorBox from '../components/ErrorBox';

const HomeScreen = () => {
    const [tabIndex, setTabIndex] = useState(0);
    const [data, setData] = useState([]);
    const [liveData, setLiveData] = useState(null);
    const [modes, setModes] = useState(null);
    const [packTypes, setPackTypes] = useState(null);
    const { credentials } = useSelector(state => state.credentialsSlice);
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [liveError, setLiveError] = useState(false);
    const [dataError, setDataError] = useState(false);

    const [liveDataLoading, setLiveDataLoading] = useState(false);
    const [dataLoading, setDataLoading] = useState(true);

    // SERVICE FILTER (empty array = all services)
    const [selectedServices, setSelectedServices] = useState([]);

    const [dates, setDates] = useState({
        to: moment(new Date()).format("yyyy-MM-DD"),
        from: moment().subtract(10, "days").format("yyyy-MM-DD"),
    });

    const [startDateForCalendar, setStartDateForCalendar] = useState(
        moment().subtract(10, "days").toDate()
    );

    const [endDateForCalendar, setEndDateForCalendar] = useState(new Date());

    const handleTabChanged = (indexValue) => {
        setTabIndex(indexValue);
    };

    const handleSearch = async () => {
        try {
            setDataLoading(true);
            setDataError(false);

            const response = await fetchAllServiceRevenueApi({ startDate: dates?.from, endDate: dates?.to }, credentials?.token);
            setData(response?.data?.result || []);
            setDataLoading(false);
            setDataError(false);

        } catch (error) {
            setDataLoading(false);

            if (error?.response?.status === 403) {
                dispatch(removeCredentials());
                navigate("/");
            }
            else {
                setDataError(true);
                toast.error(error?.response?.data?.message || "Failed to fetch revenue data");
            }
        }
    }

    // const fetchLiveData = async () => {
    //     try {
    //         setLiveError(false);

    //         setLiveDataLoading(true);
    //         const response = await fetchAllServiceLiveRevenueApi(credentials?.token);
    //         setLiveData(response?.data?.result);
    //         setLiveDataLoading(false);
    //         setLiveError(false);


    //     } catch (error) {
    //         setLiveDataLoading(false);

    //         if (error?.response?.status === 403) {
    //             dispatch(removeCredentials());
    //             navigate("/");
    //         }
    //         else {
    //             setLiveError(true);
    //             toast.error(error?.response?.data || error?.response?.data?.message);
    //         }
    //     }
    // }

    // CONVERT DATE FUNCTIONS...
    const convertStartDate = (utcDate) => {
        setStartDateForCalendar(utcDate);
        setDates({
            ...dates,
            from: moment(new Date(utcDate)).format("yyyy-MM-DD"),
        });
    };

    const convertEndDate = (utcDate) => {
        setEndDateForCalendar(utcDate);
        setDates({ ...dates, to: moment(new Date(utcDate)).format("yyyy-MM-DD") });
    };

    useEffect(() => {
        // fetchLiveData();
        handleSearch();
    }, [dates]);

    // SERVICE OPTIONS — unique services from the fetched data, sorted by name
    const serviceOptions = useMemo(() => {
        const map = new Map();
        (data || []).forEach((row) => {
            if (!map.has(row.service_id)) {
                map.set(row.service_id, { label: row.service_name, value: row.service_id });
            }
        });
        return [...map.values()].sort((a, b) => a.label.localeCompare(b.label));
    }, [data]);

    // Drop selections that no longer exist after the date range changes
    useEffect(() => {
        const available = new Set(serviceOptions.map((option) => option.value));
        setSelectedServices((prev) => {
            const next = prev.filter((id) => available.has(id));
            return next.length === prev.length ? prev : next;
        });
    }, [serviceOptions]);

    // FILTERED DATA — used by both the table and the Excel export
    const filteredData = useMemo(() => {
        if (!selectedServices.length) return data || [];
        return (data || []).filter((row) => selectedServices.includes(row.service_id));
    }, [data, selectedServices]);

    const header = (
        <ExportRevenueToExcel
            data={[...filteredData]}
            handleTabChanged={handleTabChanged}
            tabIndex={tabIndex}
        />
    );

    return (
        <>
            <Layout active={1}>
                <div className={classes.sub_container}>
                    <Header serviceDetails={null} active={1} />
                    <div className={classes.main}>
                        <div className={classes.selectors}>
                            <Calendar id="startDate" placeholder="Start Date" value={startDateForCalendar}
                                onChange={(e) => convertStartDate(e.value)} showIcon
                                maxDate={endDateForCalendar}
                            />
                            <Calendar id="endDate" placeholder="End Date" value={endDateForCalendar}
                                onChange={(e) => convertEndDate(e.value)} showIcon
                                minDate={startDateForCalendar}
                                maxDate={new Date()}
                            />
                            <MultiSelect
                                value={selectedServices}
                                options={serviceOptions}
                                onChange={(e) => setSelectedServices(e.value)}
                                placeholder="All Services"
                                display="chip"
                                filter
                                filterPlaceholder="Search services"
                                showClear
                                maxSelectedLabels={3}
                                disabled={dataLoading || !serviceOptions.length}
                                className={classes.service_filter}
                            />
                            <button type="button" className={classes.search_button}
                                disabled={liveDataLoading || dataLoading}
                                onClick={async () => {
                                    // await fetchLiveData();
                                    await handleSearch();
                                }}
                            >
                                Search
                            </button>
                        </div>

                        {/* {liveDataLoading ? <div style={{ display: "grid", placeContent: "center", width: "100%" }}>
                            <Spinner width={48} height={48} border={8} />
                        </div> : liveError ?
                            <ErrorBox fetchLiveData={fetchLiveData} handleSearch={handleSearch} />
                            :
                            <div className={classes.grid_live_data_boxes}>
                                <div className={classes.grid_live_data_box}>
                                    <div className={classes.grid_live_data_box_icon_container}>
                                        <FaChartColumn className={classes.grid_live_data_box_icon} />
                                    </div>
                                    <div className={classes.grid_live_data_box_desc}>
                                        <p className={classes.grid_live_data_box_desc_text}>Day Subscriptions</p>
                                        <h1 className={classes.grid_live_data_box_desc_value}>{liveData?.new_subscriptions}</h1>
                                    </div>
                                </div>

                                <div className={classes.grid_live_data_box}>
                                    <div className={classes.grid_live_data_box_icon_container}>
                                        <FaUserCheck className={classes.grid_live_data_box_icon} />
                                    </div>
                                    <div className={classes.grid_live_data_box_desc}>
                                        <p className={classes.grid_live_data_box_desc_text}>Day Renewals</p>
                                        <h1 className={classes.grid_live_data_box_desc_value}>{liveData?.renewals}</h1>
                                    </div>
                                </div>

                                <div className={classes.grid_live_data_box}>
                                    <div className={classes.grid_live_data_box_icon_container}>
                                        <FaUserCheck className={classes.grid_live_data_box_icon} />
                                    </div>
                                    <div className={classes.grid_live_data_box_desc}>
                                        <p className={classes.grid_live_data_box_desc_text}>Day Revenue</p>
                                        <h1 className={classes.grid_live_data_box_desc_value}>{liveData?.day_revenue}</h1>
                                    </div>
                                </div>

                                <div className={classes.grid_live_data_box}>
                                    <div className={classes.grid_live_data_box_icon_container}>
                                        <FaStarOfLife className={classes.grid_live_data_box_icon} />
                                    </div>
                                    <div className={classes.grid_live_data_box_desc}>
                                        <p className={classes.grid_live_data_box_desc_text}>Monthly Renewals</p>
                                        <h1 className={classes.grid_live_data_box_desc_value}>{liveData?.renewals_month}</h1>
                                    </div>
                                </div>

                                <div className={classes.grid_live_data_box}>
                                    <div className={classes.grid_live_data_box_icon_container}>
                                        <FaRepeat className={classes.grid_live_data_box_icon} />
                                    </div>
                                    <div className={classes.grid_live_data_box_desc}>
                                        <p className={classes.grid_live_data_box_desc_text}>Monthly Revenue</p>
                                        <h1 className={classes.grid_live_data_box_desc_value}>{liveData?.total_revenue}</h1>
                                    </div>
                                </div>

                            </div>

                        } */}

                        {dataLoading ?
                            <div style={{ display: "grid", placeContent: "center", width: "100%" }}>
                                <Spinner width={48} height={48} border={8} />
                            </div> : <>

                                {/* <div className={classes.grid_live_data_boxes}>
                                    <div className={classes.grid_service_data_box}>
                                        <div className={classes.grid_live_data_box_desc}>
                                            <p className={classes.grid_live_data_box_desc_text}>Modes of Subscription</p>
                                            <div className={classes.modes_container}>
                                                {serviceDetails?.mode &&
                                                    JSON.parse(serviceDetails.mode)?.map((mode, index) => (
                                                        <div key={index} className={classes.mode_box}>
                                                            {mode}
                                                        </div>
                                                    ))
                                                }
                                            </div>
                                        </div>
                                    </div>

                                    <div className={classes.grid_service_data_box}>
                                        <div className={classes.grid_live_data_box_desc}>
                                            <p className={classes.grid_live_data_box_desc_text}>Service Pack Details</p>
                                            <div className={classes.modes_container}>
                                                {serviceDetails?.pack_type &&
                                                    JSON.parse(serviceDetails.pack_type)?.map((pack, index) => (
                                                        <div key={index} className={classes.mode_box}>
                                                            {pack?.pack} - {pack.price} {pack.currency}
                                                        </div>
                                                    ))
                                                }
                                            </div>
                                        </div>
                                    </div>

                                    {serviceDetails?.aggregator &&
                                        <div className={classes.grid_service_data_box}>
                                            <div className={classes.grid_live_data_box_desc}>
                                                <p className={classes.grid_live_data_box_desc_text}>Aggregator</p>
                                                <div className={classes.modes_container}>
                                                    <div className={classes.mode_box}>
                                                        {serviceDetails?.aggregator}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    }

                                </div> */}

                                <div className={classes.table_container}>
                                    <RevenueTable data={filteredData} />
                                </div>
                            </>
                        }

                    </div>

                </div>
            </Layout >
        </>

    )
}

export default HomeScreen;